const ALIGNMENT_KEY = "un_alignment_v1";

export const EMPTY_ALIGNMENT = {
  experience: "",
  growth: "",
  contribution: "",
  source: "",
  updatedAt: null,
};

export const getAlignment = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ALIGNMENT_KEY) || "null");
    return parsed && typeof parsed === "object"
      ? { ...EMPTY_ALIGNMENT, ...parsed }
      : EMPTY_ALIGNMENT;
  } catch {
    return EMPTY_ALIGNMENT;
  }
};

export const saveAlignment = (alignment) => {
  const next = {
    ...EMPTY_ALIGNMENT,
    ...alignment,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(ALIGNMENT_KEY, JSON.stringify(next));
  return next;
};

const hasAny = (value, words) => {
  const haystack = String(value || "").toLowerCase();
  return words.some((word) => haystack.includes(word));
};

const BASE_CONTRIBUTION = [
  {
    id: "local-service",
    title: "Offer one useful hour",
    body: "Look for a local organization where one hour of your time or skill could make something easier for another person.",
    kind: "Contribution",
  },
  {
    id: "skill-share",
    title: "Share what you already know",
    body: "Mentor, teach, translate, explain, introduce, or make a small resource from knowledge you already carry.",
    kind: "Contribution",
  },
  {
    id: "build-something",
    title: "Build a small bridge",
    body: "Create or join a practical project that connects people to information, care, opportunity, or one another.",
    kind: "Contribution",
  },
];

export const getContributionAvenues = (text) => {
  const custom = [];

  if (hasAny(text, ["health", "care", "insurance", "medicare", "doctor", "access", "older", "senior"])) {
    custom.push({
      id: "health-access",
      title: "Improve access or navigation",
      body: "Explore patient-navigation, benefits education, senior support, transportation, digital access, or mission-driven healthcare projects that reduce friction for people trying to get care.",
      kind: "Contribution",
    });
  }

  if (hasAny(text, ["child", "student", "school", "learn", "teach", "education", "young"])) {
    custom.push({
      id: "education",
      title: "Make learning easier for someone",
      body: "Tutor, mentor, support a classroom or library, share a skill, or help someone navigate an educational opportunity.",
      kind: "Contribution",
    });
  }

  if (hasAny(text, ["animal", "cat", "dog", "pet", "wildlife"])) {
    custom.push({
      id: "animals",
      title: "Care for something vulnerable",
      body: "Consider shelter support, fostering, transport, supply drives, neighborhood animal care, or skills-based help for a rescue.",
      kind: "Contribution",
    });
  }

  if (hasAny(text, ["environment", "nature", "park", "river", "climate", "garden", "outdoor"])) {
    custom.push({
      id: "earth",
      title: "Contribute where you live",
      body: "Look for a cleanup, trail, garden, habitat, conservation, or neighborhood project where visible care creates shared benefit.",
      kind: "Contribution",
    });
  }

  if (hasAny(text, ["lonely", "community", "people", "connection", "elder", "neighbor", "isolation"])) {
    custom.push({
      id: "connection",
      title: "Reduce someone else's isolation",
      body: "Volunteer in a setting built around companionship, welcoming, calls, visits, peer support, or helping someone feel included.",
      kind: "Contribution",
    });
  }

  const merged = [...custom, ...BASE_CONTRIBUTION];
  const seen = new Set();
  return merged.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const experienceAvenues = (text) => {
  if (!String(text || "").trim()) return [];
  return [
    {
      id: "experience-small-version",
      title: "Make a smaller version possible sooner",
      body: "Ask what 10% of this experience could look like this month. A day trip, one dinner, one afternoon, one reservation, one conversation — enough to make it real without turning it into pressure.",
      kind: "Experience",
    },
    {
      id: "experience-fund",
      title: "Make a little room for it in the present",
      body: "Keep it in Look Forward and, if it fits, set aside a little time, attention, or money for it. Money is optional.",
      kind: "Experience",
    },
  ];
};

const growthAvenues = (text) => {
  if (!String(text || "").trim()) return [];
  return [
    {
      id: "growth-first-session",
      title: "Try one learning session",
      body: "Choose one book chapter, short class, tutorial, practice session, or conversation. You do not need to commit to a whole identity change to begin learning.",
      kind: "Learning & Growth",
    },
    {
      id: "growth-human",
      title: "Learn from a person",
      body: "Find someone already doing this well and ask one thoughtful question, join a small community, or observe how the work is actually done.",
      kind: "Learning & Growth",
    },
  ];
};

/**
 * Return a small, mixed set of optional avenues across Experience, Learning & Growth and
 * Contribution. The app should eventually replace generic suggestions with
 * live/local opportunities only when the user explicitly asks for them.
 */
export const getAlignmentAvenues = (alignment = EMPTY_ALIGNMENT) => {
  const all = [
    ...experienceAvenues(alignment.experience),
    ...growthAvenues(alignment.growth),
    ...getContributionAvenues(alignment.contribution),
  ];

  const priority = ["Experience", "Learning & Growth", "Contribution"];
  const chosen = [];

  // First pass: one from each populated direction.
  priority.forEach((kind) => {
    const item = all.find((avenue) => avenue.kind === kind && !chosen.some((x) => x.id === avenue.id));
    if (item) chosen.push(item);
  });

  // Second pass: fill without overwhelming the user.
  all.forEach((item) => {
    if (chosen.length >= 6) return;
    if (!chosen.some((x) => x.id === item.id)) chosen.push(item);
  });

  return chosen;
};
